import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fmtDateIST } from '../../lib/utils';
import { Sun, Moon, LogIn, Zap, Wallet, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '../../session/store';
import { ROUTES } from '../../routes/constants';
import { isEmbedded } from '../../app/config';
import { useUIStore } from '../../store';
import { FundsWidget } from '../../pages/broker/FundsWidget';
import { useBrokerCurrent, useMySubscription } from '../../api/hooks';
import { brokerApi } from '../../api/brokerClient';
import { HEADER_OAUTH_BROKER_NAMES, resolveHeaderTokenLabel, performHeaderOAuthRedirect, isHeaderTokenActionable } from './headerBrokerToken';

function LiveClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  return (
    <span className="font-mono text-xs tabular-nums text-txt-secondary tracking-wide">
      {format(t, 'dd MMM · HH:mm:ss')}
    </span>
  );
}

export default function Header() {
  if (isEmbedded) {
    return (
      <header className="h-10 flex items-center justify-between px-4 bg-bg-surface border-b border-bg-border shrink-0">
        <span />
        <span className="hidden sm:block"><LiveClock /></span>
      </header>
    );
  }

  return <StandardHeader />;
}

function StandardHeader() {
  const { user, workspace } = useSessionStore();
  const navigate = useNavigate();
  const { data: brokerStatus } = useBrokerCurrent();
  const { data: subscription } = useMySubscription();
  const [tokenBusy, setTokenBusy] = useState(false);
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);
  const queryClient = useQueryClient();
  const currentUserId = useSessionStore((s) => s.user?.id);

  const { theme, toggleTheme } = useUIStore();
  const role    = useSessionStore((s) => s.role);
  const initial = (user?.name || user?.username || 'U')[0].toUpperCase();
  const brokerName = (brokerStatus?.active_broker || '').toLowerCase();
  const storedTokenStatus = brokerStatus?.token_status || 'NO_BROKER';
  const isAdminRole = role === 'admin';
  const canGenerateToken = !isAdminRole && Boolean(brokerStatus?.has_active_broker && brokerName);
  const needsLiveCheck = canGenerateToken && storedTokenStatus === 'ACTIVE';
  const {
    isSuccess: brokerFundsHealthy,
    isLoading: brokerFundsHealthLoading,
    isFetching: brokerFundsHealthFetching,
  } = useQuery({
    queryKey: ['brokerFundsHealth', currentUserId ?? null, brokerStatus?.active_broker ?? null],
    queryFn: brokerApi.getFunds,
    enabled: needsLiveCheck,
    staleTime: 5 * 60_000,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
  // Lifecycle authority (storedTokenStatus, backend-owned) and operational
  // funds health (this live probe) are deliberately kept separate — no
  // failure-count fallback. The backend already owns authoritative expiry
  // (brokerApi.getFunds's shared 200/null validation rejects an unusable
  // payload before it ever reaches here), so a failed/unavailable ACTIVE
  // probe must show as unhealthy, not silently continue as "valid".
  const isTokenValid = canGenerateToken && storedTokenStatus === 'ACTIVE' && brokerFundsHealthy;
  // Actionability is independent of funds health: Generate session/token is
  // reachable only when the stored lifecycle itself is non-ACTIVE.
  const canTakeTokenAction = isHeaderTokenActionable({ canGenerateToken, storedTokenStatus });
  const tokenLabel = resolveHeaderTokenLabel({
    brokerName,
    canGenerateToken,
    isChecking: brokerFundsHealthLoading || brokerFundsHealthFetching,
    storedTokenStatus,
    isTokenValid,
  });
  const tokenButtonClass = !canGenerateToken
    ? 'border-bg-border text-txt-muted bg-bg-hover/20 cursor-not-allowed'
    : isTokenValid
      ? 'border-bull/20 text-bull bg-bull/5'
      : storedTokenStatus !== 'ACTIVE'
        ? 'border-amber/35 text-amber bg-amber/10 hover:bg-amber/15'
        : 'border-bear/35 text-bear bg-bear/10 hover:bg-bear/15';
  const statusDotClass = !canGenerateToken
    ? 'bg-txt-muted'
    : isTokenValid
      ? 'bg-bull'
      : storedTokenStatus !== 'ACTIVE'
        ? 'bg-amber'
        : 'bg-bear';

  async function handleTokenAction() {
    if (!canTakeTokenAction || tokenBusy) return;
    setTokenBusy(true);
    try {
      if (HEADER_OAUTH_BROKER_NAMES.has(brokerName)) {
        await performHeaderOAuthRedirect({
          brokerName,
          getOAuthUrl: brokerApi.getOAuthUrl,
          redirect: (url) => { window.location.href = url; },
        });
      } else if (brokerName === 'angelone') {
        const data = await brokerApi.testConnection({ broker_name: brokerName.toUpperCase() });
        if (data?.oauth_url) {
          window.location.href = data.oauth_url;
        } else if (data?.success) {
          queryClient.invalidateQueries({ queryKey: ['brokerStatus'] });
          queryClient.invalidateQueries({ queryKey: ['brokerFundsHealth'] });
        }
      } else if (brokerName === 'kotakneo') {
        navigate('/broker');
      }
    } catch (err) {
      console.error('Token action failed', err);
    } finally {
      setTokenBusy(false);
    }
  }

  const validTill = subscription?.valid_till || subscription?.validTill || subscription?.expires_at || null;
  const subscriptionLabel = validTill
    ? `Plan valid till ${fmtDateIST(validTill)}`
    : (subscription?.status === 'expired' ? 'Subscription expired' : 'No subscription');

  return (
    <>
    <header className="app-header h-12 md:h-14 flex items-center justify-between px-3 md:px-5 bg-bg-surface border-b border-bg-border shrink-0 shadow-inset-t">
      <div className="flex items-center gap-2.5 min-w-0">
        {workspace === 'admin' ? (
          <span className="badge bg-amber/15 border border-amber/40 text-amber text-2xs shrink-0">ADMIN</span>
        ) : null}
      </div>

      {/* Center: live stats */}
      <div className="hidden sm:flex items-center gap-3 text-2xs">
        {!isAdminRole && <FundsWidget variant="minimal" />}
        {!isAdminRole && (
          <button
            type="button"
            onClick={handleTokenAction}
            disabled={!canTakeTokenAction || tokenBusy}
            className={`token-pill h-10 w-28 md:w-36 rounded-md border text-xs transition-colors shrink-0 ${tokenButtonClass}`}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              {isTokenValid ? <Zap size={12} /> : <LogIn size={12} />}
              {tokenBusy ? 'Opening…' : tokenLabel}
            </span>
          </button>
        )}
        <span className="text-xs text-txt-muted truncate shrink-0">
          {subscriptionLabel}
        </span>
      </div>

      {/* Right: Theme toggle + User */}
      <div className="flex items-center gap-2.5">
        {/* Mobile-only broker/token/funds/plan status trigger — hidden at sm+ where the desktop cluster above already shows this */}
        <button
          type="button"
          onClick={() => setStatusSheetOpen(true)}
          aria-label={`Broker status: ${tokenLabel}`}
          className="sm:hidden relative flex items-center justify-center w-11 h-11 -m-1.5 rounded-lg hover:bg-bg-hover text-txt-muted hover:text-txt-secondary transition-colors shrink-0"
        >
          <Wallet size={15} />
          <span className={`absolute top-2.5 right-2.5 w-2 h-2 rounded-full ring-2 ring-bg-surface ${statusDotClass}`} />
        </button>
        <button
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="w-11 h-11 -m-2 flex items-center justify-center sm:w-auto sm:h-auto sm:m-0 sm:p-1.5 rounded-lg hover:bg-bg-hover text-txt-muted hover:text-txt-secondary transition-colors shrink-0"
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </button>
        <Link to={ROUTES.PROFILE} className="hidden sm:flex items-center gap-2.5 text-right hover:opacity-80 transition-opacity">
          <div className="leading-tight">
            <div className="text-xs font-medium text-txt-primary">{user?.name || user?.username || 'User'}</div>
            <div className="text-2xs text-txt-muted font-mono mt-0.5">
              {[user?.email, user?.phone].filter(Boolean).join(' • ') || '—'}
            </div>
          </div>
          {role && <span className="px-1.5 py-0.5 rounded bg-bg-hover border border-bg-border-strong text-txt-muted text-2xs">{role}</span>}
        </Link>
        {/* Brand-accented identity chip. `brand` defaults to the amber tokens, so
            this is unchanged on the AlgoSys portal. Functional colours elsewhere in
            the header (ADMIN badge, broker token pill, status dots) stay as-is. */}
        <Link to={ROUTES.PROFILE} className="w-8 h-8 rounded-lg bg-brand/15 border border-brand/25 flex items-center justify-center shrink-0 shadow-amber/5 hover:bg-brand/25 transition-colors">
          <span className="text-brand text-xs font-bold font-mono">{initial}</span>
        </Link>
      </div>
    </header>

    {/* Mobile status sheet — bottom sheet shell copied from AppShell.jsx's MoreDrawer for visual/interaction consistency */}
    {statusSheetOpen && (
      <div className="sm:hidden fixed inset-0 z-50">
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setStatusSheetOpen(false)}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-bg-card border-t border-bg-border rounded-t-2xl max-h-[82vh] flex flex-col animate-slide-up safe-area-pb">
          <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
            <span className="text-sm font-medium text-txt-primary">Broker & Plan Status</span>
            <button
              onClick={() => setStatusSheetOpen(false)}
              aria-label="Close"
              className="w-11 h-11 -m-2.5 flex items-center justify-center rounded-lg hover:bg-bg-hover text-txt-muted"
            >
              <X size={16} />
            </button>
          </div>

          <div className="overflow-y-auto flex-1 px-4 py-2 space-y-4">
            {/* Broker + token status */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-txt-secondary">
                {brokerStatus?.active_broker || 'No broker connected'}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-2xs font-medium ${tokenButtonClass}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusDotClass}`} />
                {tokenBusy ? 'Opening…' : tokenLabel}
              </span>
            </div>

            {/* Funds — reuse the existing FundsWidget component as-is */}
            {!isAdminRole && <FundsWidget variant="vertical" />}

            {/* Generate token — reuses the same handleTokenAction already defined above, same disabled condition as the desktop button */}
            {!isAdminRole && (
              <button
                type="button"
                onClick={handleTokenAction}
                disabled={!canTakeTokenAction || tokenBusy}
                className={`w-full h-11 rounded-md border text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${tokenButtonClass}`}
              >
                {isTokenValid ? <Zap size={13} /> : <LogIn size={13} />}
                {tokenBusy ? 'Opening…' : tokenLabel}
              </button>
            )}

            {/* Plan validity */}
            <div className="flex items-center justify-between pt-2 border-t border-bg-border/60">
              <span className="text-xs text-txt-muted">Plan</span>
              <span className="text-xs text-txt-secondary">{subscriptionLabel}</span>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
